/**
 * Done. / ChiroCPD — CertCard
 *
 * Renders a branded certificate of completion card with a Print / Save as PDF button.
 * Used on the dedicated /cert/:moduleId page and inline after quiz submission.
 */

import type { ModuleCertificate } from '@/lib/types';

function openPrintWindow(cert: ModuleCertificate): void {
  const esc = (s: string): string =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const dateStr = new Date(cert.completedAt).toLocaleDateString('en-NZ', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Certificate — ${esc(cert.moduleTitle)}</title><style>
body{font-family:Georgia,serif;padding:60px;max-width:700px;margin:0 auto;color:#111}
.brand{display:flex;align-items:center;gap:8px;margin-bottom:32px;padding-bottom:20px;border-bottom:1px solid #e5e7eb}
.brand-name{font-family:"Arial Narrow",Arial,sans-serif;font-weight:900;font-size:18px;letter-spacing:-0.5px;color:#1a5c3a;text-transform:uppercase}
.box{border:2px solid #1a5c3a;border-radius:12px;padding:48px}
h2{font-size:22px;text-transform:uppercase;color:#1a5c3a;margin:0 0 24px}
.row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #f3f4f6;font-size:14px}
.lbl{color:#6b7280}.val{font-weight:600}.mono{font-family:monospace;font-size:12px}
@media print{body{padding:20px}}
</style></head><body><div class="box">
<div class="brand"><span class="brand-name">Done. CPD</span></div>
<h2>Certificate of Completion</h2>
<div class="row"><span class="lbl">Recipient</span><span class="val">${esc(cert.recipientName)}</span></div>
<div class="row"><span class="lbl">Module</span><span class="val">${esc(cert.moduleTitle)}</span></div>
<div class="row"><span class="lbl">CPD Hours</span><span class="val">${String(cert.hours)}</span></div>
<div class="row"><span class="lbl">Completed</span><span class="val">${dateStr}</span></div>
<div class="row"><span class="lbl">Certificate ID</span><span class="val mono">${esc(cert.certId)}</span></div>
</div></body></html>`;
  const w = window.open('', '_blank', 'width=820,height=640');
  if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => { w.print(); }, 400); }
}

interface CertCardProps {
  cert: ModuleCertificate;
}

export function CertCard({ cert }: CertCardProps): React.ReactElement {
  const dateStr = new Date(cert.completedAt).toLocaleDateString('en-NZ', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className="flex flex-col gap-4">
      <div
        data-cert-card
        className="border-2 border-[var(--stitch-primary)] rounded-xl p-8 bg-[var(--stitch-primary-fixed)]/20"
      >
        {/* Done. CPD brand header */}
        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-[var(--stitch-primary)]/20">
          <span className="material-symbols-outlined text-[20px] text-[var(--stitch-primary)]">clinical_notes</span>
          <span className="stitch-font-display text-[16px] font-bold tracking-tighter text-[var(--stitch-primary)] uppercase">Done. CPD</span>
        </div>
        <div className="flex items-center gap-3 mb-4">
          <span className="material-symbols-outlined text-[var(--stitch-primary)] text-3xl is-filled">workspace_premium</span>
          <h3 className="stitch-font-headline text-[20px] uppercase text-[var(--stitch-primary)]">Certificate of Completion</h3>
        </div>
        <dl className="grid grid-cols-2 gap-y-2 stitch-font-body text-[14px]">
          <dt className="text-[var(--stitch-on-surface-variant)]">Recipient</dt>
          <dd className="text-right font-medium">{cert.recipientName}</dd>
          <dt className="text-[var(--stitch-on-surface-variant)]">Module</dt>
          <dd className="text-right font-medium">{cert.moduleTitle}</dd>
          <dt className="text-[var(--stitch-on-surface-variant)]">CPD Hours</dt>
          <dd className="text-right font-medium">{cert.hours}</dd>
          <dt className="text-[var(--stitch-on-surface-variant)]">Completed</dt>
          <dd className="text-right font-medium">{dateStr}</dd>
          <dt className="text-[var(--stitch-on-surface-variant)]">Certificate ID</dt>
          <dd className="text-right font-mono text-[12px]">{cert.certId}</dd>
        </dl>
      </div>

      <button
        type="button"
        onClick={() => { openPrintWindow(cert); }}
        className="self-start flex items-center gap-2 bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] px-6 py-3 rounded-full stitch-font-label text-[13px] uppercase tracking-wider font-bold hover:opacity-90 transition-opacity"
      >
        <span className="material-symbols-outlined text-lg">download</span>
        Download / Print PDF
      </button>
    </div>
  );
}
